"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.as = exports.withOptions = exports.withVariants = exports.extend = exports.CBC_OPTIONS = void 0;
var React = require("react");
var classnames_1 = require("classnames");
exports.CBC_OPTIONS = '__cbc_options';
/**
 * Enhances a partial options object with defaults
 */
function withDefaultOptions(options) {
    return __assign({ className: undefined, displayName: undefined, variants: undefined, elementType: 'div' }, options);
}
/**
 * Creates a `ClassBoundComponent` with the options object provided in `options`
 *
 * @param   options     Options for the ClassBoundComponent
 * @returns             ClassBoundComponent
 */
function createClassBoundComponentFromOptions(options) {
    var ElementTypeSafe = (options.elementType || 'div');
    var shouldForwardRef = typeof ElementTypeSafe === 'string' ||
        ElementTypeSafe.$$typeof === Symbol.for('react.forward_ref');
    var render = (function () { return function (_a, ref) {
        var customClassName = _a.className, restProps = __rest(_a, ["className"]);
        var _b = splitProps(restProps, options.variants || {}), componentProps = _b.componentProps, variantProps = _b.variantProps;
        var variantClassNames = makeVariantClassNames(options.variants || {}, variantProps);
        var componentClassName = classnames_1.default(options.className, variantClassNames, customClassName);
        var props = __assign({ className: componentClassName.length < 1 ? undefined : componentClassName }, componentProps);
        if (shouldForwardRef) {
            props.ref = ref;
        }
        return React.createElement(ElementTypeSafe, props);
    }; })();
    var ComposedComponent = (shouldForwardRef
        ? React.forwardRef(render)
        : render);
    ComposedComponent.displayName = options.displayName;
    ComposedComponent[exports.CBC_OPTIONS] = options;
    return ComposedComponent;
}
var extend = function (component, className, displayNameOrVariants, maybeVariants) {
    var displayName = typeof displayNameOrVariants === 'string'
        ? displayNameOrVariants
        : undefined;
    var variants = typeof displayNameOrVariants === 'object'
        ? displayNameOrVariants
        : maybeVariants || {};
    var options = component[exports.CBC_OPTIONS];
    return createClassBoundComponentFromOptions({
        className: mergeClassValues(options.className, className),
        displayName: displayName,
        variants: mergeVariants(options.variants || {}, variants),
        elementType: options.elementType,
    });
};
exports.extend = extend;
/**
 * Creates a new ClassBoundComponent with the same options as this ClassBoundComponent
 * except the variants being extended with `variants`. While existing variants remain,
 * new variants override old variants if they're named similarly.
 *
 * @param     component   Component to enhance with variants
 * @param     variants    Variants to merge into this component's variants
 * @returns               ClassBoundComponent with merged variants
 */
function withVariants(component, variants, displayName) {
    var options = component[exports.CBC_OPTIONS];
    var mergedVariants = __assign(__assign({}, options.variants), variants);
    return createClassBoundComponentFromOptions(__assign(__assign({}, options), { displayName: displayName || options.displayName, variants: mergedVariants }));
}
exports.withVariants = withVariants;
/**
 * Creates a new ClassBoundComponent with the same options as this ClassBoundComponent
 * except the `elementType` being set to the `elementType` from the parameters.
 *
 * @param     component     Component to convert
 * @param     elementType   New element type of ClassBoundComponent
 * @returns                 ClassBoundComponent with modified elementType
 */
function as(component, elementType, displayName) {
    var options = component[exports.CBC_OPTIONS];
    return createClassBoundComponentFromOptions(__assign(__assign({}, options), { displayName: displayName || options.displayName, elementType: elementType }));
}
exports.as = as;
/**
 * Creates a new ClassBoundComponent with options provided by the `transformOptions` argument
 * which will receive this ClassBoundComponent's options.
 *
 * @param     component         Component to modify
 * @param     transformOptions  Function mapping options of this ClassBoundComponent to the new options
 * @returns                     ClassBoundComponent with options returned by `transformOptions`
 */
function withOptions(component, transformOptions) {
    var prevOptions = component[exports.CBC_OPTIONS];
    var nextOptions = transformOptions(prevOptions);
    return createClassBoundComponentFromOptions(withDefaultOptions(nextOptions));
}
exports.withOptions = withOptions;
var createClassBoundComponent = function (optionsOrClassName, displayNameOrVariants, variantsOrElementType, elementType) {
    return createClassBoundComponentFromOptions(argumentsToOptions(optionsOrClassName, displayNameOrVariants, variantsOrElementType, elementType));
};
/**
 * Converts the positional arguments of the overloaded function signatures to an options object
 */
function argumentsToOptions(optionsOrClassName, displayNameOrVariants, variantsOrElementType, elementType) {
    var _a;
    if (typeof optionsOrClassName === 'object') {
        return optionsOrClassName;
    }
    if (typeof displayNameOrVariants === 'object') {
        return {
            className: optionsOrClassName,
            displayName: undefined,
            variants: displayNameOrVariants,
            elementType: (_a = variantsOrElementType !== null && variantsOrElementType !== void 0 ? variantsOrElementType : elementType) !== null && _a !== void 0 ? _a : 'div',
        };
    }
    return {
        className: optionsOrClassName,
        displayName: displayNameOrVariants,
        variants: variantsOrElementType || {},
        elementType: elementType,
    };
}
/**
 * Creates an array of ClassValues of those variants that are enabled in the props
 * @param     variants    Variants object
 * @param     props       Props to look for variants in
 * @returns               Array of ClassValues to add to the component
 */
function makeVariantClassNames(variants, props) {
    var classNames = [];
    for (var variantName in variants) {
        if (props[variantName]) {
            classNames.push(variants[variantName]);
        }
    }
    return classNames;
}
/**
 * Splits a combined props object so that the variant flags are separated out
 * @param     props       Combined props object
 * @param     variants    Variants object
 * @returns               Object with `variantProps` and `componentProps`
 */
function splitProps(props, variants) {
    var componentProps = __assign({}, props);
    var variantProps = {};
    for (var variantName in variants) {
        if (props.hasOwnProperty(variantName)) {
            variantProps[variantName] = props[variantName];
            delete componentProps[variantName];
        }
    }
    return { componentProps: componentProps, variantProps: variantProps };
}
function mergeClassValues(value1, value2) {
    return value1 && value2 ? [value1, value2] : value1 || value2;
}
function mergeVariants(v1, v2) {
    return Object.keys(__assign(__assign({}, v1), v2)).reduce(function (merged, variantName) {
        merged[variantName] = mergeClassValues(v1[variantName], v2[variantName]);
        return merged;
    }, {});
}
createClassBoundComponent.extend = extend;
createClassBoundComponent.withVariants = withVariants;
createClassBoundComponent.withOptions = withOptions;
createClassBoundComponent.as = as;
var wrappedInProxy = (Proxy
    ? new Proxy(createClassBoundComponent, {
        get: function (target, property) {
            if (Object.prototype.hasOwnProperty.call(target, property)) {
                return target[property];
            }
            return function () {
                var args = [];
                for (var _i = 0; _i < arguments.length; _i++) {
                    args[_i] = arguments[_i];
                }
                return createClassBoundComponentFromOptions(__assign(__assign({}, argumentsToOptions.apply(void 0, args)), { elementType: property }));
            };
        },
    })
    : createClassBoundComponent);
exports.default = wrappedInProxy;



//////////////////
// WEBPACK FOOTER
// ./node_modules/class-bound-components/dist/index.js
// module id = 58
// module chunks = 0